import { useEffect, useMemo, useRef } from "react";
import { Select } from "components/ui";
import { useTranslation } from "translation/useTranslation";

/**
 * The current value is always kept as an option even if not in the active list; changing the grade
 * clears the level when the new grade has no matching levels.
 */
export default function PayGradeSelect({
  activePayfactorValues,
  grade,
  level,
  onGradeChange,
  onLevelChange,
  gradeError,
  levelError,
}) {
  const { translate } = useTranslation();
  const previousGradeRef = useRef(grade);

  const rowsForGrade = useMemo(
    () => (activePayfactorValues || []).filter((row) => row.grade === grade),
    [activePayfactorValues, grade]
  );

  const gradeOptions = useMemo(() => {
    const map = new Map();
    (activePayfactorValues || []).forEach((row) => {
      if (!row?.grade || map.has(row.grade)) return;
      map.set(row.grade, { value: row.grade, label: row.grade_label || row.grade });
    });
    const arr = Array.from(map.values());
    if (grade && !arr.some((o) => o.value === grade)) arr.push({ value: grade, label: grade });
    return arr;
  }, [activePayfactorValues, grade]);

  const levelOptions = useMemo(() => {
    const map = new Map();
    rowsForGrade.forEach((row) => {
      if (!row?.level || map.has(row.level)) return;
      map.set(row.level, { value: row.level, label: row.level_label || row.level });
    });
    const arr = Array.from(map.values());
    if (level && !arr.some((o) => o.value === level)) arr.push({ value: level, label: level });
    return arr;
  }, [rowsForGrade, level]);

  const hasLevelOptions = levelOptions.length > 0;

  useEffect(() => {
    if (previousGradeRef.current !== grade && rowsForGrade.length > 0 && !hasLevelOptions && level) {
      onLevelChange('');
    }
    previousGradeRef.current = grade;
  }, [grade, level, rowsForGrade, hasLevelOptions, onLevelChange]);

  return (
    <>
      <div className="d-flex align-items-center">
        <div style={{ display: 'contents' }} data-testid="pay-grade-select">
          <Select
            options={gradeOptions}
            className="w-100"
            error={!!gradeError}
            onChange={(opt) => onGradeChange(opt?.value ?? '')}
            value={gradeOptions.find((o) => o.value === grade) || null}
            isClearable
            placeholder={translate('form.select_placeholder')}
          />
        </div>
        {(hasLevelOptions || !!level) && (
          <>
            <span className="mx-2">/</span>
            <div style={{ display: 'contents' }} data-testid="pay-grade-level-select">
              <Select
                options={levelOptions}
                className="w-100"
                error={!!levelError}
                onChange={(opt) => onLevelChange(opt?.value ?? '')}
                value={levelOptions.find((o) => o.value === level) || null}
                isClearable
                placeholder={translate('form.select_placeholder')}
              />
            </div>
          </>
        )}
      </div>
      {(gradeError || levelError) && (
        <div className="d-block invalid-feedback">
          {gradeError}{gradeError && levelError ? ' / ' : ''}{levelError}
        </div>
      )}
    </>
  );
}
